/**
 * Single source of truth for the visual treatment of each ReplayEventType.
 *
 * Pages + components branch on these instead of hard-coding event-type
 * strings inside JSX. `filterable: true` means the type shows up as a
 * chip in the filter bar; `false` (lifecycle events) is always rendered.
 *
 * `tone` names a class in styles.css (`ev--photo`, `ev--verdict`, …) that
 * sets `--ev` and `--ev-tint` locally, so a marker, a chip and a quote bar
 * all read their colour from the same place and both themes come for free.
 * Nothing here carries a hex value.
 */
import type { ReplayEventType } from '../../api/replay';

export type EventTone = 'life' | 'photo' | 'verdict' | 'retry' | 'voice' | 'alert';

export interface EventStyle {
  /** Palette class suffix: `ev--${tone}` sets --ev and --ev-tint. */
  tone: EventTone;
  /** Short label for chips, rows and tooltips. */
  label: string;
  /** Whether this event type appears in the filter bar (true = filterable). */
  filterable: boolean;
}

export const EVENT_STYLES: Record<ReplayEventType, EventStyle> = {
  'session.started': { tone: 'life', label: 'Session started', filterable: false },
  'step.started': { tone: 'life', label: 'Step started', filterable: false },
  'step.photo_captured': { tone: 'photo', label: 'Photo', filterable: true },
  'step.verified': { tone: 'verdict', label: 'Verdict', filterable: true },
  'step.retry': { tone: 'retry', label: 'Retry', filterable: true },
  worker_dialogue: { tone: 'voice', label: 'Worker voice', filterable: true },
  safety_alert: { tone: 'alert', label: 'Safety alert', filterable: true },
  'session.ended': { tone: 'life', label: 'Session ended', filterable: false },
};

/** Filterable types in display order. Drives the chip bar + EventList. */
export const FILTERABLE_TYPES: ReplayEventType[] = [
  'step.photo_captured',
  'step.verified',
  'step.retry',
  'worker_dialogue',
  'safety_alert',
];

/** `ev--photo` etc. One helper so no component builds the string itself. */
export function toneClass(type: ReplayEventType): string {
  return `ev--${EVENT_STYLES[type].tone}`;
}
